import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  Stack,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Autocomplete,
  TextField,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Header from '../components/Layout/Header';
import Sidebar from '../components/Layout/Sidebar';
import { classService, studentService, attendanceService } from '../services/apiService';
import { showToast } from '../utils/toast';
import { formatDate } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';
import { getAvatarColor, getInitials } from '../utils/avatar';

const Attendance = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const drawerWidth = 280;
  const { user, isTeacher, isAdmin } = useAuth();

  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedClassName, setSelectedClassName] = useState('');
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [isHeadTeacher, setIsHeadTeacher] = useState(false);
  const [isMarked, setIsMarked] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Generate last 7 days for date dropdown
  const today = new Date();
  const getLast7Days = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      days.push({
        value: dateStr,
        label: formatDate(dateStr),
        isToday: i === 0,
      });
    }
    return days;
  };

  const dateOptions = getLast7Days();
  const [selectedDate, setSelectedDate] = useState(dateOptions[0].value);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await classService.getAll();
        let allClasses = res.data || res || [];
        
        // For teachers, filter to only show classes where they are head teacher
        if (isTeacher()) {
          const userId = user?.id || user?.user_id;
          allClasses = allClasses.filter((c) => c.head_teacher_id === userId);
        }
        
        setClasses(allClasses);
      } catch (err) {
        // Silent fail for GET operations
      } finally {
        setLoading(false);
      }
    })();
  }, [isTeacher, user]);

  const handleView = useCallback(async () => {
    if (!selectedClassId) {
      showToast.info('Please select a class');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const rosterRes = await attendanceService.getAll({ class_id: selectedClassId, date: selectedDate });
      const roster = rosterRes.data || rosterRes || [];
      let mapped = [];
      let marked = false;
      if (Array.isArray(roster) && roster.length > 0 && roster[0].student_id) {
        mapped = roster.map((r) => ({
          id: r.student_id,
          full_name: r.full_name,
          roll_number: r.roll_number,
          status: r.status || 'Present',
        }));
        marked = true;
      } else {
        const res = await studentService.getAll();
        const all = res.data || res || [];
        const list = all.filter((s) => String(s.class_id) === String(selectedClassId));
        mapped = list.map((s) => ({
          id: s.id,
          full_name: s.full_name,
          roll_number: s.roll_number,
          status: 'Present',
        }));
        marked = false;
      }
      setRows(mapped);
      setIsMarked(marked);
    } catch (err) {
      setError(err?.message || 'Failed to load students');
    } finally {
      setLoading(false);
    }
  }, [selectedClassId, selectedDate]);

  const handleToggle = (studentId) => () => {
    setRows((prev) => prev.map((r) => {
      if (r.id === studentId) {
        return { ...r, status: r.status === 'Present' ? 'Absent' : 'Present' };
      }
      return r;
    }));
  };

  const handleSubmitClick = useCallback(() => {
    if (rows.length === 0) {
      showToast.info('No students to submit');
      return;
    }
    
    const absentStudents = rows.filter((r) => r.status === 'Absent');
    
    // If all are present, show message and submit directly
    if (absentStudents.length === 0) {
      setConfirmDialogOpen(true);
      return;
    }
    
    // Show confirmation modal with absent students
    setConfirmDialogOpen(true);
  }, [rows]);

  const handleConfirmSubmit = useCallback(async () => {
    try {
      setSubmitting(true);
      setConfirmDialogOpen(false);
      const payload = {
        class_id: selectedClassId,
        date: selectedDate,
        records: rows.map((r) => ({ student_id: r.id, status: r.status })),
      };
      await attendanceService.create(payload);
      showToast.success('Attendance submitted successfully');
      setIsMarked(true);
    } catch (err) {
      showToast.error(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to submit attendance');
    } finally {
      setSubmitting(false);
    }
  }, [rows, selectedClassId, selectedDate]);

  const presentCount = useMemo(() => rows.filter((r) => r.status === 'Present').length, [rows]);
  const absentCount = useMemo(() => rows.filter((r) => r.status === 'Absent').length, [rows]);
  const totalCount = useMemo(() => rows.length, [rows]);
  const absentStudents = useMemo(() => rows.filter((r) => r.status === 'Absent'), [rows]);
  const allPresent = useMemo(() => absentCount === 0 && totalCount > 0, [absentCount, totalCount]);


  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          drawerWidth={drawerWidth}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
              display: 'flex',
              flexDirection: 'column',
              pb: { xs: 20, md: 20 },
            }}
          >
          <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Typography 
              variant="h5" 
              gutterBottom 
              sx={{ 
                fontWeight: 700, 
                mb: { xs: 2, md: 3 },
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
              }}
            >
              Attendance Management
            </Typography>

            {/* Show message and hide everything if teacher has no classes */}
            {isTeacher() && classes.length === 0 ? (
              <Paper elevation={1} sx={{ p: { xs: 3, sm: 4 }, mb: { xs: 2, md: 3 }, borderRadius: 2 }}>
                <Alert severity="info" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  You are not the head teacher of any class. Only head teachers can mark attendance.
                </Alert>
              </Paper>
            ) : (
              <>
                {/* Top Section - Class Selection */}
                <Paper elevation={1} sx={{ p: { xs: 2, sm: 2.5 }, mb: { xs: 2, md: 3 }, borderRadius: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 1.5, sm: 2 }, 
                    alignItems: { xs: 'stretch', sm: 'center' }, 
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexWrap: 'wrap' 
                  }}>
                    <Autocomplete
                      options={classes}
                      getOptionLabel={(option) => option ? `Class ${option.name}${option.section ? ` - ${option.section}` : ''}` : ''}
                      value={classes.find((c) => c.id === selectedClassId) || null}
                      onChange={(event, newValue) => {
                        const selected = newValue;
                        setSelectedClassId(newValue ? newValue.id : '');
                        setSelectedClassName(selected ? `${selected.name}${selected.section ? ` - ${selected.section}` : ''}` : '');
                        setSelectedClass(selected || null);
                        // Check if current user is head teacher (only for teachers)
                        if (isTeacher() && selected) {
                          const userId = user?.id || user?.user_id;
                          const isHead = selected.head_teacher_id === userId;
                          setIsHeadTeacher(isHead);
                        } else if (isAdmin()) {
                          // Admin can always mark attendance
                          setIsHeadTeacher(true);
                        } else {
                          setIsHeadTeacher(false);
                        }
                        // Clear rows when class changes
                        setRows([]);
                        setIsMarked(false);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Class"
                          placeholder="Search and select class..."
                          size="small"
                        />
                      )}
                      sx={{ minWidth: { xs: '100%', sm: 200, md: 220 }, flex: { xs: '1 1 auto', sm: '0 1 auto' } }}
                      ListboxProps={{
                        style: {
                          maxHeight: 200,
                        },
                      }}
                      noOptionsText="No classes found"
                    />
                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160, md: 180 }, flex: { xs: '1 1 auto', sm: '0 1 auto' } }}>
                      <InputLabel>Select Date</InputLabel>
                      <Select
                        value={selectedDate}
                        label="Select Date"
                        onChange={(e) => {
                          setSelectedDate(e.target.value);
                          // Clear rows when date changes
                          setRows([]);
                          setIsMarked(false);
                        }}
                      >
                        {dateOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label} {option.isToday ? '(Today)' : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      onClick={handleView}
                      disabled={!selectedClassId || loading || (isTeacher() && !isHeadTeacher)}
                      sx={{ 
                        minWidth: { xs: '100%', sm: 120 },
                        mt: { xs: 1, sm: 0 }
                      }}
                    >
                      View Students
                    </Button>
                  </Box>
                </Paper>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                {/* Show message if teacher is not head teacher */}
                {isTeacher() && selectedClassId && !isHeadTeacher && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    You are not the head teacher of this class. Only head teachers can mark attendance.
                  </Alert>
                )}

                {/* Students List */}
                {rows.length === 0 && !loading ? (
                  <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: 1, borderColor: 'divider' }}>
                    <Typography variant="body1" color="text.secondary">
                      {selectedClassId
                        ? 'No students found for this class. Click "View Students" to load.'
                        : 'Please select a class and click "View Students" to see attendance options.'}
                    </Typography>
                  </Paper>
                ) : loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    {/* Desktop: Table view */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        border: 1,
                        borderColor: 'divider',
                        mb: { xs: 20, md: 20 },
                        display: { xs: 'none', md: 'block' },
                        flex: 1,
                        overflow: 'auto',
                        minHeight: 0,
                      }}
                    >
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.secondary' }}>
                                Roll Number
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.secondary' }}>
                                Student Name
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.secondary', textAlign: 'center' }}>
                                Status
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rows.map((row) => {
                              const isPresent = row.status === 'Present';
                              return (
                                <TableRow key={row.id} hover>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={600}>
                                      {row.roll_number || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={700}>
                                      {row.full_name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Button
                                      variant={isPresent ? 'contained' : 'outlined'}
                                      color={isPresent ? 'success' : 'error'}
                                      size="small"
                                      onClick={handleToggle(row.id)}
                                      startIcon={isPresent ? <CheckCircleIcon /> : <CancelIcon />}
                                      sx={{
                                        fontWeight: 700,
                                        minWidth: 120,
                                        textTransform: 'none',
                                      }}
                                    >
                                      {isPresent ? 'Present' : 'Absent'}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>

                    {/* Mobile: Card view */}
                    <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 20, flex: 1, overflow: 'auto', minHeight: 0 }}>
                      <Stack spacing={1.5}>
                        {rows.map((row) => {
                          const isPresent = row.status === 'Present';
                          return (
                            <Paper
                              key={row.id}
                              variant="outlined"
                              onClick={handleToggle(row.id)}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                borderColor: isPresent ? 'success.main' : 'error.main',
                                borderWidth: 1.5,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: 2,
                                },
                                '&:active': {
                                  transform: 'translateY(0)',
                                },
                              }}
                            >
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Avatar
                                  src={row.profile_image || null}
                                  alt={row.full_name}
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: getAvatarColor(row.full_name),
                                    color: '#ffffff',
                                    fontWeight: 700,
                                  }}
                                >
                                  {!row.profile_image && getInitials(row.full_name)}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="subtitle1" fontWeight={700}>
                                    {row.full_name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                    Roll: {row.roll_number || '—'}
                                  </Typography>
                                </Box>
                                <Button
                                  variant={isPresent ? 'contained' : 'outlined'}
                                  color={isPresent ? 'success' : 'error'}
                                  size="small"
                                  startIcon={isPresent ? <CheckCircleIcon /> : <CancelIcon />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggle(row.id)();
                                  }}
                                  sx={{
                                    fontWeight: 700,
                                    minWidth: 100,
                                    textTransform: 'none',
                                  }}
                                >
                                  {isPresent ? 'Present' : 'Absent'}
                                </Button>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Box>

                    {/* Summary and Submit Section - Fixed at bottom */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2, sm: 2.5, md: 3 },
                        borderRadius: { xs: '16px 16px 0 0', md: '16px 16px 0 0' },
                        border: 1,
                        borderColor: 'divider',
                        borderBottom: 'none',
                        bgcolor: 'background.paper',
                        position: 'fixed',
                        bottom: 0,
                        left: { xs: 0, md: `${drawerWidth}px` },
                        right: 0,
                        zIndex: 1000,
                        boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
                      }}
                    >
                      <Stack spacing={1.5}>
                        {/* Class Name and Date */}
                        {selectedClassName && (
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'text.primary',
                              fontWeight: 700,
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            }}
                          >
                            {selectedClassName} · {formatDate(selectedDate)}
                          </Typography>
                        )}

                        {/* Summary Stats and Submit Button - Same line on desktop */}
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={2}
                          justifyContent="space-between"
                          alignItems={{ xs: 'stretch', sm: 'center' }}
                        >
                          {/* Chips - All in one line */}
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
                          >
                            <Chip
                              label={`Total: ${totalCount}`}
                              size="small"
                              sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: '24px',
                              }}
                            />
                            <Chip
                              label={`Present: ${presentCount}`}
                              size="small"
                              sx={{
                                bgcolor: 'success.main',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: '24px',
                              }}
                            />
                            <Chip
                              label={`Absent: ${absentCount}`}
                              size="small"
                              sx={{
                                bgcolor: 'error.main',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: '24px',
                              }}
                            />
                          </Stack>

                          {/* Submit Button */}
                          <Button
                            variant="contained"
                            size="large"
                            onClick={handleSubmitClick}
                            disabled={rows.length === 0 || submitting || (isTeacher() && !isHeadTeacher)}
                            sx={{
                              minWidth: { xs: '100%', sm: 200 },
                              px: { xs: 2, sm: 4 },
                              fontWeight: 700,
                            }}
                          >
                            {submitting ? (
                              <>
                                <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                                Submitting...
                              </>
                            ) : (
                              isMarked ? 'Update Attendance' : 'Mark Attendance'
                            )}
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Box>
                )}
              </>
            )}
          </Box>
          </Box>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={700}>
            Confirm Attendance Submission
          </Typography>
        </DialogTitle>
        <DialogContent>
          {allPresent ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body1" fontWeight={600}>
                All students are present!
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Every student in {selectedClassName} is marked as present for {formatDate(selectedDate)}.
              </Typography>
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Please review the list of absent students before submitting:
                </Typography>
              </Alert>
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {absentStudents.map((student) => (
                  <ListItem key={student.id} divider>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight={600}>
                          {student.full_name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          Roll No: {student.roll_number || '—'}
                        </Typography>
                      }
                    />
                    <Chip
                      label="Absent"
                      color="error"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                Submitting...
              </>
            ) : (
              'Confirm & Submit'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Attendance;
